import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon, PauseIcon, PlayIcon, RotateCcwIcon, SparklesIcon, ShieldIcon, ChevronsRightIcon } from 'lucide-react';
import { beats, type ArtifactTab } from '../../lib/script';
import { excludedCriteria } from '../../lib/data';
import { ArtifactPane } from './ArtifactPane';
type DemoViewProps = {
  initialText: string;
  onExit: () => void;
};
const STAGES = ['Comprendre', 'Construire', 'Évaluer'];
export function DemoView({
  initialText,
  onExit
}: DemoViewProps) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [manualTab, setManualTab] = useState<ArtifactTab | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const atEnd = index >= beats.length - 1;

  // Moteur de lecture. Chaque beat porte son propre délai.
  useEffect(() => {
    if (!playing || atEnd) return;
    const timer = setTimeout(() => setIndex(i => i + 1), beats[index].delay);
    return () => clearTimeout(timer);
  }, [index, playing, atEnd]);

  // État du panneau d'artefacts : accumulé depuis le début.
  const state = useMemo(() => {
    let profileCount = 0;
    let showDescription = false;
    let criteriaCount = 0;
    let lastTab: ArtifactTab | null = null;
    const tabs: ArtifactTab[] = [];
    let stage = STAGES[0];
    beats.slice(0, index + 1).forEach(b => {
      if (b.profileBlocks !== undefined) profileCount = b.profileBlocks;
      if (b.showDescription) showDescription = true;
      if (b.criteriaCount !== undefined) criteriaCount = b.criteriaCount;
      if (b.stage) stage = b.stage;
      if (b.openTab) {
        lastTab = b.openTab;
        if (!tabs.includes(b.openTab)) tabs.push(b.openTab);
      }
    });
    return {
      profileCount,
      showDescription,
      criteriaCount,
      tabs,
      lastTab,
      stage
    };
  }, [index]);
  const activeTab: ArtifactTab = manualTab ?? state.lastTab ?? 'profil';

  // L'onglet suit la conversation, sauf si l'utilisateur en a choisi un.
  useEffect(() => {
    setManualTab(null);
  }, [state.lastTab]);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [index]);
  const visibleBeats = beats.slice(0, index + 1);
  const nextIsAssistant = !atEnd && beats[index + 1]?.role === 'assistant';
  return <div className="flex h-screen w-full flex-col bg-canvas" data-id="element-114" data-mp="1d21qm682hofh">
      {/* Barre de contrôle de la démonstration */}
      <header className="flex flex-none items-center justify-between gap-4 border-b border-line bg-white px-5 py-3 sm:px-6" data-id="element-115" data-mp="pdigrm9dd3la">
        <div className="flex min-w-0 items-center gap-3" data-id="element-116" data-mp="kfgxb081729a">
          <button type="button" onClick={onExit} aria-label="Retour au tableau de bord" className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-117" data-mp="z2s6wwknkc6r">
            <ArrowLeftIcon className="h-[18px] w-[18px]" strokeWidth={1.75} data-id="element-118" data-mp="2d9zpf189o40g" />
          </button>
          <div className="hidden items-center gap-2 sm:flex" data-id="element-119" data-mp="2i1q5lntvxlzs">
            {STAGES.map(s => {
            const done = STAGES.indexOf(s) < STAGES.indexOf(state.stage);
            const current = s === state.stage;
            return <span key={s} className="flex items-center gap-2" data-id="element-120" data-mp="28bybsvtm0wwd">
                  <span className={`text-[12px] ${current ? 'font-500 text-brand' : done ? 'text-ink' : 'text-muted'}`} data-id="element-121" data-mp="2w0pv0xp824x0">
                    {s}
                  </span>
                  {s !== STAGES[STAGES.length - 1] && <span className="h-px w-4 bg-line" data-id="element-122" data-mp="2r1d687esinrs" />}
                </span>;
          })}
          </div>
        </div>

        <div className="flex flex-none items-center gap-1.5" data-id="element-123" data-mp="1bjkwvwdbwzqx">
          <button type="button" onClick={() => {
          setIndex(0);
          setPlaying(true);
        }} className="flex items-center gap-1.5 rounded-card px-2.5 py-1.5 text-[12.5px] text-muted transition-colors hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-124" data-mp="1fn960jhm03ea">
            <RotateCcwIcon className="h-[14px] w-[14px]" strokeWidth={1.75} data-id="element-125" data-mp="1h5rbltm8z423" />
            Rejouer
          </button>
          {!atEnd && <>
              <button type="button" onClick={() => setPlaying(p => !p)} className="flex items-center gap-1.5 rounded-card px-2.5 py-1.5 text-[12.5px] text-muted transition-colors hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-126" data-mp="2eqdtg02hahr5">
                {playing ? <PauseIcon className="h-[14px] w-[14px]" strokeWidth={1.75} data-id="element-127" data-mp="pdtmil7fq6vi" /> : <PlayIcon className="h-[14px] w-[14px]" strokeWidth={1.75} data-id="element-128" data-mp="311f6v95bw82m" />}
                {playing ? 'Pause' : 'Reprendre'}
              </button>
              <button type="button" onClick={() => setIndex(i => Math.min(i + 1, beats.length - 1))} className="flex items-center gap-1.5 rounded-card border border-line bg-white px-2.5 py-1.5 text-[12.5px] text-ink transition-colors hover:border-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-129" data-mp="350o08cwgi3xn">
                <ChevronsRightIcon className="h-[14px] w-[14px] text-muted" strokeWidth={1.75} data-id="element-130" data-mp="3ssbk4avaweqf" />
                Suivant
              </button>
            </>}
        </div>
      </header>

      {/* Conversation 60 % · Artefacts 40 % */}
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[60fr_40fr]" data-id="element-131" data-mp="1yfd9tr9lpi33">
        <div ref={scrollRef} className="min-h-0 overflow-y-auto px-5 py-6 sm:px-8" data-id="element-132" data-mp="23clkz7wc0luu">
          <div className="mx-auto flex w-full max-w-[680px] flex-col gap-5" data-id="element-133" data-mp="36o14z35zuufe">
            {visibleBeats.map((beat, i) => {
            const text = i === 0 ? initialText || beat.text : beat.text;
            if (beat.role === 'user') {
              return <div key={beat.id} className="flex justify-end" data-id="element-134" data-mp="3fmmgied1t96o">
                    <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-white px-4 py-3 text-[15px] leading-[1.55] text-ink" data-id="element-135" data-mp="3ap4dt5pmjq74">
                      {text}
                    </p>
                  </div>;
            }
            return <div key={beat.id} className="flex gap-3" data-id="element-136" data-mp="2am3exc7nykc" data-mpi={i}>
                  <div className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-brand-soft text-brand" data-id="element-137" data-mp="2c6xymyizhi7">
                    <SparklesIcon className="h-[18px] w-[18px]" strokeWidth={1.75} data-id="element-138" data-mp="c80hwrekqda0" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-139" data-mp="79p98k00m8td">
                    <p className="text-[15px] leading-[1.55] text-ink" data-id="element-140" data-mp="7b0aod5zn4hb">{text}</p>

                    {beat.chips && <div className="mt-3 flex flex-wrap gap-2" data-id="element-141" data-mp="vu23hbrvhthx">
                        {beat.chips.map(chip => <span key={chip} className="rounded-full border border-line bg-white px-2.5 py-1 text-[12px] font-500 text-ink" data-id="element-142" data-mp="gxb0ksizpznk">
                            {chip}
                          </span>)}
                      </div>}

                    {beat.showExcluded && <div className="mt-3 rounded-card bg-brand-soft px-4 py-3" data-id="element-143" data-mp="3uch5hwjucq9b">
                        <p className="flex items-center gap-1.5 text-[10px] font-600 tracking-[0.09em] text-ink" data-id="element-144" data-mp="354ujz9jx91u">
                          <ShieldIcon className="h-3.5 w-3.5 text-brand" strokeWidth={2} data-id="element-145" data-mp="3lareusxs9vcl" />
                          CRITÈRES QUE JE N'UTILISERAI PAS
                        </p>
                        <ul className="mt-2 space-y-1" data-id="element-146" data-mp="9mg5gejboueq">
                          {excludedCriteria.map(item => <li key={item.label} className="text-[13px] leading-[1.4] text-ink" data-id="element-147" data-mp="4nd9rkp8g0jf">
                              <span className="font-500" data-id="element-148" data-mp="3vt4yqgr9x74n">{item.label}</span>
                              <span className="text-muted" data-id="element-149" data-mp="3quuz9tyinkd8"> — {item.reason}</span>
                            </li>)}
                        </ul>
                      </div>}
                  </div>
                </div>;
          })}

            {playing && nextIsAssistant && <div className="flex gap-3" data-id="element-150" data-mp="3d4htny93khr2">
                <div className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-brand-soft text-brand" data-id="element-151" data-mp="3d13qinsm04ik">
                  <SparklesIcon className="h-[18px] w-[18px]" strokeWidth={1.75} data-id="element-152" data-mp="335s9fytqal7n" />
                </div>
                <div className="flex items-center gap-1 pt-3" data-id="element-153" data-mp="2z35hzz0yz2az">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted" data-id="element-154" data-mp="2z61edix65ixb" />
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted [animation-delay:0.15s]" data-id="element-155" data-mp="1enpgxrb0mkzj" />
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted [animation-delay:0.3s]" data-id="element-156" data-mp="1ip2ms1e3yo6g" />
                </div>
              </div>}

            {atEnd && <div className="rounded-card border border-line bg-white px-4 py-3" data-id="element-157" data-mp="1sjbdcjjkw71z">
                <p className="text-[13px] leading-[1.45] text-muted" data-id="element-158" data-mp="uz8d1sp35fqf">
                  Fin du lot 1. La suite — sourcing, mur d'anonymat, doubles consentements, entrevues,
                  résultat à 90 jours et apprentissage — s'ajoute à cette même séquence.
                </p>
              </div>}
          </div>
        </div>

        <div className="hidden min-h-0 lg:block" data-id="element-159" data-mp="c7fi3sqghkg2">
          <ArtifactPane tab={activeTab} onTabChange={setManualTab} availableTabs={state.tabs} profileCount={state.profileCount} showDescription={state.showDescription} criteriaCount={state.criteriaCount} data-id="element-160" data-mp="c6mjxg5kc39j" />
        </div>
      </div>
    </div>;
}